import { Box, DialogContent, Stack, type Theme } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { useNearbyCancellationProtectionPayouts } from "@src/appV2/redesign/CancellationProtectionPayout/api/useNearbyCancellationProtectionPayouts";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import {
  PageHeaderWrapper,
  Tab,
  Tabs,
  Text,
  Title,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect, useState } from "react";

import { CancellationProtectionTermsList } from "./CancellationProtectionTermsList";
import { useCancellationProtectionConfig } from "./hooks/useCancellationProtectionConfig";
import { OthersReceivingProtectionList } from "./OthersReceivingProtectionList";

interface CancellationProtectionTermsDialogProps {
  modalState: UseModalState;
}

const TERMS_TAB = { label: "Terms", value: "terms" } as const;
const PAYOUTS_TAB = { label: "Meet the Workers", value: "payouts" } as const;
const MIN_RESULTS_TO_SHOW_PAYOUTS_TAB = 5;

type TabValue = typeof TERMS_TAB.value | typeof PAYOUTS_TAB.value;

export function CancellationProtectionTermsDialog(props: CancellationProtectionTermsDialogProps) {
  const { modalState } = props;
  const [activeTab, setActiveTab] = useState<TabValue>("terms");
  const worker = useDefinedWorker();
  const cancellationProtectionConfig = useCancellationProtectionConfig();
  const isBannerEnabled =
    cancellationProtectionConfig.enabled && cancellationProtectionConfig.isBannerEnabled;

  useLogEffect(
    APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_TERMS_DIALOG_VIEWED,
    { workerId: worker.userId },
    { enabled: modalState.modalIsOpen }
  );

  const { payouts, totalCountLabel, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useNearbyCancellationProtectionPayouts({
      workplaceMsa: worker.address?.metropolitanStatisticalArea,
      enabled: isBannerEnabled && modalState.modalIsOpen,
    });

  const shouldShowPayoutsTab = payouts.length >= MIN_RESULTS_TO_SHOW_PAYOUTS_TAB;
  const tabs: Array<{ label: string; value: TabValue }> = shouldShowPayoutsTab
    ? [TERMS_TAB, PAYOUTS_TAB]
    : [TERMS_TAB];

  useEffect(() => {
    if (!shouldShowPayoutsTab && activeTab === PAYOUTS_TAB.value) {
      setActiveTab(TERMS_TAB.value);
    }
  }, [shouldShowPayoutsTab, activeTab]);

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow onClickBack={modalState.closeModal} />
      </PageHeaderWrapper>

      <Box>
        <Stack spacing={2} sx={{ paddingBottom: 8, paddingX: 5 }}>
          <Title
            component="h1"
            variant="h3"
            sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
          >
            Cancellation Protection
          </Title>
          <Text variant="body2" sx={(theme: Theme) => ({ color: theme.palette.text.tertiary })}>
            Know that your time is always valued and your earnings protected when a workplace
            cancels you last minute
          </Text>
        </Stack>

        <Tabs
          variant="underlined"
          value={activeTab}
          sx={{
            "& .MuiTabs-flexContainer": { gap: 6, paddingX: 5 },
            "& .MuiTab-root": {
              flexGrow: 0,
              paddingX: 0,
              minWidth: "auto",
            },
            marginBottom: "-2px",
          }}
          onChange={(_, value: string) => {
            let tab: TabValue;
            // eslint-disable-next-line unicorn/prefer-ternary -- Better readability than ternary
            if (value === TERMS_TAB.value || value === PAYOUTS_TAB.value) {
              tab = value;
            } else {
              tab = TERMS_TAB.value;
            }
            setActiveTab(tab);
            logEvent(APP_V2_USER_EVENTS.CANCELLATION_PROTECTION_TERMS_DIALOG_TAB_CHANGED, {
              workerId: worker.userId,
              tab,
            });
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.value} variant="underlined" label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      </Box>

      <DialogContent
        sx={(theme: Theme) => ({
          flex: 1,
          minHeight: 0,
          backgroundColor: theme.palette.background.quaternary,
          "&.MuiDialogContent-root": { paddingX: 0, paddingY: 0 },
        })}
      >
        {activeTab === TERMS_TAB.value ? (
          <Stack sx={{ paddingX: 5, paddingTop: 9, paddingBottom: 11 }}>
            <CancellationProtectionTermsList variant="overview" />
          </Stack>
        ) : null}
        {activeTab === PAYOUTS_TAB.value ? (
          <OthersReceivingProtectionList
            payouts={payouts}
            totalCountLabel={totalCountLabel}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
          />
        ) : null}
      </DialogContent>
    </FullScreenDialog>
  );
}
